import { Injectable } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { TripType } from '@constants/home.constants';
import { PassengerTypeCode } from '@models/booking.models';
import { DatesFormConfig, StationsFormConfig } from '@models/form/form.models';
import { SearchRequest } from '@models/requests/search-request.model';
import { Station } from '@models/responses/station-response.model';
import { isValidStationPairValidator } from '@utils/form-utils';
import { DateTime } from 'luxon';

@Injectable({
  providedIn: 'root',
})
export class SearchWidgetFormConfigService {
  constructor(private formBuilder: FormBuilder) {}

  generateSearchWidgetForm(
    stationsList: Station[],
    searchRequest?: SearchRequest,
    tripType?: TripType,
    clearDestination: boolean = false,
    requirePromoCodes: boolean = false,
  ): FormGroup {
    const tripTypePromoCode = this.formBuilder.group({
      tripType: [tripType ?? TripType.roundTrip, [Validators.required]],
      promoCode: [
        searchRequest?.promotionCode ?? '',
        [Validators.maxLength(8)],
      ],
    });

    if(requirePromoCodes) {
      tripTypePromoCode.controls['promoCode'].addValidators(Validators.required)
    }

    const stations = this.formBuilder.array(
      [] as FormGroup<StationsFormConfig>[],
    );
    const dates = this.formBuilder.array([] as FormGroup<DatesFormConfig>[]);

    if (searchRequest && searchRequest.criteria?.length > 0 && searchRequest.criteria[0]) {
      switch (tripType) {
        case TripType.oneWay: {
          stations.push(
            this.formBuilder.nonNullable.group(
              {
                originStationName: new FormControl(
                  stationsList.find(
                    (st) =>
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      st.stationCode === searchRequest.criteria[0]!.origin,
                  )?.fullName ?? '',
                  { nonNullable: true, validators: [Validators.required] },
                ),
                originStationCode: new FormControl(
                  searchRequest.criteria[0].origin ?? '',
                  { nonNullable: true, validators: [Validators.required] },
                ),
                destinationStationName: new FormControl(
                  stationsList.find(
                    (st) =>
                      st.stationCode ===
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        searchRequest.criteria[0]!.destination &&
                      !clearDestination,
                  )?.fullName ?? '',
                  { nonNullable: true, validators: [Validators.required] },
                ),
                destinationStationCode: new FormControl(
                  searchRequest.criteria[0].destination && !clearDestination
                    ? searchRequest.criteria[0].destination
                    : '',
                  { nonNullable: true, validators: [Validators.required] },
                ),
              },
              {
                validators: [isValidStationPairValidator(stationsList)],
              },
            ),
          );
          dates.push(
            this.formBuilder.group({
              departDate: [
                DateTime.fromISO(
                  searchRequest.criteria[0].beginDate,
                )?.toJSDate(),
                [Validators.required],
              ],
              returnDate: [],
            }),
          );
          break;
        }
        case TripType.roundTrip: {
          stations.push(
            this.formBuilder.group(
              {
                originStationName: [
                  stationsList.find(
                    (st) =>
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      st.stationCode === searchRequest.criteria[0]!.origin,
                  )?.fullName ?? '',
                  [Validators.required],
                ],
                originStationCode: [
                  searchRequest.criteria[0].origin ?? '',
                  [Validators.required],
                ],
                destinationStationName: [
                  stationsList.find(
                    (st) =>
                      st.stationCode ===
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        searchRequest.criteria[0]!.destination &&
                      !clearDestination,
                  )?.fullName ?? '',
                  [Validators.required],
                ],
                destinationStationCode: [
                  searchRequest.criteria[0].destination && !clearDestination
                    ? searchRequest.criteria[0].destination
                    : '',
                  [Validators.required],
                ],
              },
              {
                validators: [isValidStationPairValidator(stationsList)],
              },
            ),
          );
          dates.push(
            this.formBuilder.group(
              {
                departDate: [
                  DateTime.fromISO(
                    searchRequest.criteria[0].beginDate,
                  )?.toJSDate(),
                  [Validators.required],
                ],
                returnDate: [
                  searchRequest.criteria[1] &&
                    DateTime.fromISO(
                      searchRequest.criteria[1].beginDate,
                    )?.toJSDate(),
                  [Validators.required],
                ],
              },
              {
                validators: [],
              },
            ),
          );
          break;
        }
        case TripType.multiCity: {
          searchRequest.criteria.forEach((c) => {
            stations.push(
              this.formBuilder.group(
                {
                  originStationName: [
                    stationsList.find((st) => st.stationCode === c.origin)
                      ?.fullName ?? '',
                    [Validators.required],
                  ],
                  originStationCode: [c.origin ?? '', [Validators.required]],
                  destinationStationName: [
                    stationsList.find(
                      (st) =>
                        st.stationCode === c.destination && !clearDestination,
                    )?.fullName ?? '',
                    [Validators.required],
                  ],
                  destinationStationCode: [
                    c.destination && !clearDestination ? c.destination : '',
                    [Validators.required],
                  ],
                },
                {
                  validators: [isValidStationPairValidator(stationsList)],
                },
              ),
            );
            dates.push(
              this.formBuilder.group({
                departDate: [
                  DateTime.fromISO(c.beginDate)?.toJSDate(),
                  [Validators.required],
                ],
                returnDate: [],
              }),
            );
          });
          break;
        }
      }
    } else {
      stations.push(this.generateStationsForm(stationsList));
      dates.push(this.generateDatesForm(tripType));
    }

    const criteria = this.formBuilder.group({
      stations,
      dates,
    });

    const searchReqPassengers = searchRequest?.passengers ?? [];

    const travelers = this.formBuilder.group({
      adultCount: [
        searchReqPassengers.find((p) => p.type === PassengerTypeCode.ADT)
          ?.count ??
          (searchRequest &&
          searchReqPassengers
            .filter((p) => p.type !== PassengerTypeCode.ADT)
            .reduce((count, paxType) => count + paxType.count, 0) !== 0
            ? 0
            : 1),
        [Validators.required],
      ],
      youthCount: [
        searchReqPassengers?.find((p) => p.type === PassengerTypeCode.OTN)
          ?.count ?? 0,
        [Validators.required],
      ],
      childCount: [
        searchReqPassengers.find((p) => p.type === PassengerTypeCode.CHD)
          ?.count ?? 0,
        [Validators.required],
      ],
      infantCount: [
        searchReqPassengers.find((p) => p.type === PassengerTypeCode.INF)
          ?.count ?? 0,
        [Validators.required],
      ],
      infantInOwnSeatCount: [
        searchReqPassengers.find((p) => p.type === PassengerTypeCode.INS)
          ?.count ?? 0,
        [Validators.required],
      ],
    });

    return this.formBuilder.group({
      tripTypePromoCode,
      criteria,
      travelers,
    });
  }

  generateStationsForm(stationsList: Station[]): FormGroup<StationsFormConfig> {
    return this.formBuilder.group(
      {
        originStationName: ['', [Validators.required]],
        originStationCode: ['', [Validators.required]],
        destinationStationName: ['', [Validators.required]],
        destinationStationCode: ['', [Validators.required]],
      },
      {
        validators: [isValidStationPairValidator(stationsList)],
      },
    );
  }

  generateDatesForm(tripType: TripType | undefined): FormGroup<DatesFormConfig> {
    return this.formBuilder.group({
      departDate: [null, [Validators.required]],
      returnDate: [
        null,
        tripType === TripType.roundTrip ? [Validators.required] : [],
      ],
    });
  }
}
